/* Mirelyae — petits composants maison (minuteur, livraison estimée, rafraîchissement au changement de variante).
   Aucun framework, chargé une seule fois en module. */
import { StandardEvents } from '@shopify/events';

/* Rafraîchit le contenu d'un bloc quand la variante change (prix, économies, stock…).
   Le HTML à jour est fourni par Horizon dans l'événement productSelect. */
class MirRefresh extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section, dialog');
    if (!this.section) return;
    this.onSelect = (event) => {
      if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
      event.promise
        ?.then(({ detail }) => {
          const fresh = detail?.html?.querySelector(`mir-refresh[data-mir-id="${this.dataset.mirId}"]`);
          if (fresh) this.innerHTML = fresh.innerHTML;
        })
        .catch(() => {});
    };
    this.section.addEventListener(StandardEvents.productSelect, this.onSelect);
  }
  disconnectedCallback() {
    this.section?.removeEventListener(StandardEvents.productSelect, this.onSelect);
  }
}
if (!customElements.get('mir-refresh')) customElements.define('mir-refresh', MirRefresh);

/* Minuteur relié à une vraie échéance (data-end = date ISO). Se masque ou affiche un texte une fois terminé. */
class MirCountdown extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loop) {
      const d = parseInt(this.dataset.loop, 10) * 1000;
      this.loop = d;
      this.key = this.dataset.persist;
      let saved = 0;
      try { saved = this.key ? parseInt(sessionStorage.getItem(this.key) || '0', 10) : 0; } catch (e) {}
      this.end = saved > Date.now() ? saved : Date.now() + d;
      try { if (this.key) sessionStorage.setItem(this.key, String(this.end)); } catch (e) {}
    } else this.end = Date.parse(this.dataset.end || '');
    if (Number.isNaN(this.end)) return this.expire();
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
    /* Panier redessiné par Horizon (suppression, quantité…) : le nouveau HTML remet « hidden » et 00:00 ;
       on corrige aussitôt, avant affichage, pour que la barre reste fixe. */
    this.obs = new MutationObserver(() => { if (!this.enCours && !this.fini) this.tick(); });
    this.obs.observe(this, { attributes: true, attributeFilter: ['hidden'], childList: true, subtree: true, characterData: true });
  }
  disconnectedCallback() {
    clearInterval(this.timer);
    this.obs?.disconnect();
  }
  tick() {
    this.enCours = true;
    try { this._tick(); } finally { this.obs?.takeRecords(); this.enCours = false; }
  }
  _tick() {
    let s = Math.floor((this.end - Date.now()) / 1000);
    if (s <= 0 && this.loop) {
      this.end = Date.now() + this.loop; s = Math.floor(this.loop / 1000);
      try { if (this.key) sessionStorage.setItem(this.key, String(this.end)); } catch (e) {}
    }
    if (s <= 0) return this.expire();
    const j = Math.floor(s / 86400); s -= j * 86400;
    const h = Math.floor(s / 3600); s -= h * 3600;
    const m = Math.floor(s / 60); s -= m * 60;
    const set = (k, v) => this.querySelectorAll(`[data-u="${k}"]`).forEach((el) => (el.textContent = String(v).padStart(2, '0')));
    set('j', j); set('h', h); set('m', m); set('s', s);
    this.querySelectorAll('[data-if-days]').forEach((el) => (el.hidden = j === 0));
    this.hidden = false;
  }
  expire() {
    this.fini = true;
    clearInterval(this.timer);
    const msg = this.querySelector('[data-expired]');
    if (msg && msg.textContent.trim()) {
      this.classList.add('is-expired');
      this.hidden = false;
    } else {
      this.hidden = true;
      this.closest('[data-mir-hide-with-timer]')?.setAttribute('hidden', '');
    }
  }
}
if (!customElements.get('mir-countdown')) customElements.define('mir-countdown', MirCountdown);

/* Livraison estimée : « entre le mar. 30 sept. et le jeu. 2 oct. » en jours ouvrés (hors week-end si demandé). */
class MirDelivery extends HTMLElement {
  connectedCallback() {
    const min = parseInt(this.dataset.min || '3', 10);
    const max = parseInt(this.dataset.max || '6', 10);
    const skipWeekend = this.dataset.weekend !== 'true';
    const cutoff = parseInt(this.dataset.cutoff || '0', 10);
    const start = new Date();
    if (cutoff && start.getHours() >= cutoff) start.setDate(start.getDate() + 1);
    const add = (days) => {
      const d = new Date(start);
      let n = 0;
      while (n < days) {
        d.setDate(d.getDate() + 1);
        if (!skipWeekend || (d.getDay() !== 0 && d.getDay() !== 6)) n++;
      }
      return d;
    };
    const fmt = new Intl.DateTimeFormat(document.documentElement.lang || 'fr', { weekday: 'short', day: 'numeric', month: 'short' });
    this.querySelectorAll('[data-d="min"]').forEach((el) => (el.textContent = fmt.format(add(min))));
    this.querySelectorAll('[data-d="max"]').forEach((el) => (el.textContent = fmt.format(add(max))));
    this.hidden = false;
  }
}
if (!customElements.get('mir-delivery')) customElements.define('mir-delivery', MirDelivery);

/* Boutons « Je la veux » des sections de landing : remontent jusqu'au bouton d'achat de la fiche. */
document.addEventListener('click', (e) => {
  const trigger = e.target instanceof Element && e.target.closest('[data-mir-to-buy]');
  if (!trigger) return;
  const target = document.querySelector('main cc-offre, main product-form-component add-to-cart-component, main product-form-component');
  if (!target) return;
  e.preventDefault();
  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
});

/* FAQ : une seule question ouverte à la fois si la section le demande. */
document.addEventListener('toggle', (e) => {
  const d = e.target;
  if (!(d instanceof HTMLDetailsElement) || !d.open) return;
  const group = d.closest('[data-mir-accordion-single]');
  group?.querySelectorAll('details[open]').forEach((o) => o !== d && (o.open = false));
}, true);

/* Cartes d'offres : pilotent le sélecteur de variantes natif d'Horizon (qui met à jour prix, image, panier). */
document.addEventListener('click', (e) => {
  const card = e.target instanceof Element && e.target.closest('.mir-offres [data-mir-value]');
  if (!card) return;
  const wrap = card.closest('.mir-offres');
  const name = wrap.dataset.mirOption;
  const value = card.dataset.mirValue;
  const scope = card.closest('.shopify-section, dialog') || document;
  wrap.querySelectorAll('.mir-of').forEach((c) => { c.classList.toggle('is-actif', c === card); c.setAttribute('aria-pressed', c === card); });
  const radio = [...scope.querySelectorAll('variant-picker input[type="radio"]')].find((i) => i.dataset.optionName === name && i.value === value);
  if (radio) { if (!radio.checked) radio.click(); return; }
  const select = [...scope.querySelectorAll('variant-picker select')].find((s) => [...s.options].some((o) => o.value === value || o.textContent.trim() === value) && s.closest('[data-option-id], fieldset, .variant-option')?.textContent.includes(name));
  if (select) {
    const opt = [...select.options].find((o) => o.value === value || o.textContent.trim() === value);
    select.value = opt.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }
});

/* Bouton d'achat façon Shrine : rond de chargement immédiat jusqu'à l'ouverture du panier. */
document.addEventListener('click', (e) => {
  const btn = e.target instanceof Element && e.target.closest('main .add-to-cart-button');
  if (!btn || btn.disabled) return;
  btn.classList.add('mir-chargement');
  const fin = () => btn.classList.remove('mir-chargement');
  const dlg = document.querySelector('#cart-drawer dialog');
  if (dlg) {
    const obs = new MutationObserver(() => { if (dlg.open) { fin(); obs.disconnect(); } });
    obs.observe(dlg, { attributes: true, attributeFilter: ['open'] });
  }
  setTimeout(fin, 4000);
}, true);

/* mir-qte-auto : dans le panier, la quantité saisie est validée dès le changement ou la touche Entrée (comme Impact). */
const mirQte = (e) => {
  const i = e.target;
  if (!(i instanceof HTMLInputElement) || !i.closest('#cart-drawer .cart-items__quantity')) return;
  if (e.type === 'keydown' && e.key !== 'Enter') return;
  if (e.type === 'keydown') e.preventDefault();
  i.dispatchEvent(new FocusEvent('blur'));
};
document.addEventListener('change', mirQte, true);
document.addEventListener('keydown', mirQte, true);
